import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type LiveConversationMode,
  trackScreenView,
  trackWaitingRoomEnter,
  trackWaitingRoomExit,
} from '../../../services/analytics.service';
import {
  checkQueue,
  claimQueueSlot,
  getQueueStatus,
  leaveQueue as leaveQueueApi,
  leaveQueueBeacon,
} from '../../../services/heygenQueueService';
import logger from '../../../utils/logger';

interface QueueState {
  isInQueue: boolean;
  isQueueReady: boolean;
  queuePosition: number | null;
  totalInQueue: number | null;
  queueCountdown: number | null;
}

interface CheckQueueResult {
  canStart: boolean;
  inQueue: boolean;
  position: number;
  totalInQueue: number;
}

export interface UseHeyGenQueueOptions {
  sahabeId?: string;
  conversationId?: string;
  mode?: LiveConversationMode;
}

interface UseHeyGenQueueReturn extends QueueState {
  /** Check queue and join if needed - returns whether can start immediately */
  checkAndJoinQueue: () => Promise<CheckQueueResult>;
  leaveQueue: () => Promise<void>;
  claimSlot: () => Promise<{ success: boolean; reason?: string }>;
  /** Total seconds waited in queue before exiting/starting */
  queueWaitSeconds: number;
}

/** Polling interval for queue status (5 seconds) */
const POLL_INTERVAL_MS = 5000;

/**
 * Hook to manage HeyGen avatar session queue
 * Handles polling, state management, and queue interactions
 */
export const useHeyGenQueue = (
  _userId: string, // Kept for future use
  onSessionStart: () => Promise<void>,
  _onError: (error: string) => void,
  onQueueLeft?: () => void, // Called when user leaves queue (manual or expiration)
  options?: UseHeyGenQueueOptions,
): UseHeyGenQueueReturn => {
  // Queue state
  const [queuePosition, setQueuePosition] = useState<number | null>(null);
  const [totalInQueue, setTotalInQueue] = useState<number | null>(null);
  const [isInQueue, setIsInQueue] = useState(false);
  const [isQueueReady, setIsQueueReady] = useState(false);
  const [queueCountdown, setQueueCountdown] = useState<number | null>(null);

  // Refs for timers & analytics
  const queuePollingRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const queueEnteredAtRef = useRef<number | null>(null);
  const [queueWaitSeconds, setQueueWaitSeconds] = useState<number>(0);

  const getWaitDuration = useCallback(() => {
    if (!queueEnteredAtRef.current) return 0;
    const elapsed = Math.max(0, Math.round((Date.now() - queueEnteredAtRef.current) / 1000));
    setQueueWaitSeconds(elapsed);
    queueEnteredAtRef.current = null;
    return elapsed;
  }, []);

  // Cleanup timers helper
  const clearTimers = useCallback(() => {
    if (queuePollingRef.current) {
      clearInterval(queuePollingRef.current);
      queuePollingRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Leave queue API and cleanup
  const leaveQueue = useCallback(async () => {
    // Only track waiting room exit if the user was actually waiting in the queue
    if (isInQueue || queueEnteredAtRef.current !== null) {
      const wait = getWaitDuration();
      trackWaitingRoomExit(wait, 'user_cancel');
    }
    clearTimers();
    try {
      await leaveQueueApi();
    } catch (err) {
      logger.debug('useHeyGenQueue', 'Leave queue failed', err);
    }
    setIsInQueue(false);
    setIsQueueReady(false);
    setQueuePosition(null);
    setTotalInQueue(null);
    setQueueCountdown(null);
    // Notify parent that queue was left
    onQueueLeft?.();
  }, [clearTimers, onQueueLeft, getWaitDuration, isInQueue]);

  // Handle countdown expiration
  const handleCountdownExpired = useCallback(() => {
    logger.info('useHeyGenQueue', 'Queue countdown expired - leaving queue silently');
    const wait = getWaitDuration();
    trackWaitingRoomExit(wait, 'timeout');
    clearTimers();
    leaveQueueBeacon(); // Fire and forget leave via beacon
    setIsInQueue(false);
    setIsQueueReady(false);
    setQueuePosition(null);
    setTotalInQueue(null);
    setQueueCountdown(null);
    // Don't show error - turn expiration is expected business logic, not an application error
    // Notify parent that queue was left
    onQueueLeft?.();
  }, [clearTimers, onQueueLeft, getWaitDuration]);

  // Start countdown when user is ready
  const startCountdown = useCallback(
    (seconds: number) => {
      setQueueCountdown(seconds);

      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

      countdownIntervalRef.current = setInterval(() => {
        setQueueCountdown((prev) => {
          if (prev === null || prev <= 1) {
            handleCountdownExpired();
            return null;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [handleCountdownExpired],
  );

  // Start polling queue status
  const startQueuePolling = useCallback(() => {
    if (queuePollingRef.current) return;

    logger.info('useHeyGenQueue', 'Starting queue polling');
    queuePollingRef.current = setInterval(async () => {
      try {
        const status = await getQueueStatus();

        if (!status || status.position === 0) {
          // User was removed from queue (expired or left)
          logger.info('useHeyGenQueue', 'User removed from queue (not found or position 0)');
          setIsInQueue(false);
          setQueuePosition(null);
          setTotalInQueue(null);
          clearTimers();
          return;
        }

        logger.info('useHeyGenQueue', 'Queue status update:', {
          position: status.position,
          totalInQueue: status.totalInQueue,
          ready: status.ready,
        });

        setQueuePosition(status.position);
        setTotalInQueue(status.totalInQueue);

        // Check if user just became ready (transition to ready state)
        if (status.ready) {
          setIsQueueReady((prevIsReady) => {
            if (!prevIsReady) {
              logger.info('useHeyGenQueue', "User's turn! Starting countdown");
              startCountdown(status.countdown ?? 60);
              return true;
            }
            return prevIsReady;
          });
        }
      } catch (err) {
        logger.warn('useHeyGenQueue', 'Queue status poll failed', err);
      }
    }, POLL_INTERVAL_MS);
  }, [clearTimers, startCountdown]);

  /**
   * Check queue and join if needed - MAIN ENTRY POINT
   * Returns whether user can start immediately or was added to queue
   */
  const checkAndJoinQueue = useCallback(async (): Promise<CheckQueueResult> => {
    try {
      const result = await checkQueue();

      if (result.canStart) {
        // Can start immediately - no queue needed
        setQueueWaitSeconds(0);
        return result;
      }

      // In queue - update state, track analytics, and start polling
      queueEnteredAtRef.current = Date.now();
      trackWaitingRoomEnter(
        options?.sahabeId || 'unknown',
        options?.conversationId || 'unknown',
        options?.mode || 'video',
      );
      trackScreenView('WaitingRoom');

      setQueuePosition(result.position);
      setTotalInQueue(result.totalInQueue);
      setIsInQueue(true);
      startQueuePolling();

      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to check queue';
      throw new Error(msg);
    }
  }, [startQueuePolling, options?.sahabeId, options?.conversationId, options?.mode]);

  // Claim slot
  const claimSlot = useCallback(async () => {
    // Stop countdown and polling immediately
    clearTimers();
    setQueueCountdown(null);

    try {
      const result = await claimQueueSlot();
      if (result.success) {
        const wait = getWaitDuration();
        trackWaitingRoomExit(wait, 'connected');
        logger.info('useHeyGenQueue', 'Slot claimed successfully');
        setIsInQueue(false);
        setIsQueueReady(false);
        setQueuePosition(null);
        setTotalInQueue(null);
        await onSessionStart();
        return { success: true };
      }

      // Failed to claim
      const wait = getWaitDuration();
      const exitReason = result.reason === 'capacity_full' ? 'capacity_full' : 'error';
      trackWaitingRoomExit(wait, exitReason);
      logger.warn('useHeyGenQueue', 'Failed to claim slot', result.reason);
      setIsInQueue(false);
      setIsQueueReady(false);
      setQueuePosition(null);
      setTotalInQueue(null);
      return { success: false, reason: result.reason };
    } catch (err) {
      const wait = getWaitDuration();
      trackWaitingRoomExit(wait, 'error');
      const msg = err instanceof Error ? err.message : 'Failed to claim slot';
      return { success: false, reason: msg };
    }
  }, [clearTimers, onSessionStart, getWaitDuration]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return {
    isInQueue,
    isQueueReady,
    queuePosition,
    totalInQueue,
    queueCountdown,
    checkAndJoinQueue,
    leaveQueue,
    claimSlot,
    queueWaitSeconds,
  };
};
